import { uniqBy } from "lodash-es"
import { currencyInputSelectOptions, currencyOptions } from "./utils"
import { useMemo, useState } from "react"
import { CurrencyOption } from "./types"
import classNames from "classnames"
import { Select, FlagIcon, SelectProps } from "@oysterhr/ui"

export type CurrencySelectProps = SelectProps & {
  currencyCodes?: string[]
  className?: string
  disabledOption?: string | null
}

export const CurrencySelect = ({
  onFocus,
  value,
  onChange,
  onBlur,
  currencyCodes,
  name,
  disabled,
  placeholder,
  id,
  className,
  isClearable,
  error,
  disabledOption,
  label,
  ...rest
}: CurrencySelectProps) => {
  const [searchValue, setSearchValue] = useState("")
  const selectedCurrencyCode = useMemo<CurrencyOption | null>(
    () =>
      currencyInputSelectOptions()[1].options.find(h => h.currencyCode === value) as CurrencyOption,
    [value],
  )

  const options = useMemo(
    () =>
      currencyCodes
        ? (uniqBy(
            currencyOptions
              .filter(option => currencyCodes.includes((option as CurrencyOption).currencyCode))
              .reverse(),
            "currencyCode",
          ).reverse() as CurrencyOption[])
        : (currencyOptions as CurrencyOption[]),
    [currencyCodes],
  )

  const filteredOptions = useMemo(
    () =>
      options.filter(option => {
        const formattedSearchText = searchValue.trim().toLowerCase()
        const formattedOptionLabel = option.label.trim().toLowerCase()

        if (formattedSearchText && option.topCurrencyGroup) {
          return false
        }
        return formattedOptionLabel.includes(formattedSearchText)
      }),
    [options, searchValue],
  )

  const blurHandler = () => {
    if (onBlur) {
      onBlur()
    }
    setSearchValue("")
  }

  const changeHandler = (value: SelectProps["value"]) => {
    const selectedOption = options.find(option => option.currencyCode === value)

    onChange?.(selectedOption?.currencyCode as string)
    setSearchValue("")
  }

  return (
    <div className={classNames("relative", className)} data-testid="open-currency-chooser">
      <Select
        id={id}
        label={label}
        error={error}
        onFocus={onFocus}
        isClearable={isClearable}
        placeholder={placeholder || "Choose"}
        name={name}
        disabled={disabled}
        {...rest}
        // NOTE: This is a workaround to avoid "a component is changing an uncontrolled input to be controlled" error when you clear selected value
        value={selectedCurrencyCode ? selectedCurrencyCode.currencyCode : ""}
        onBlur={blurHandler}
        onChange={changeHandler}
      >
        <Select.Input
          placeholder="Search..."
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
        />

        {filteredOptions.map((option, idx) => {
          if (option.name.startsWith("----")) {
            return <hr key={idx} data-testid="empty-option" />
          }

          return (
            <Select.Option
              startAdornment={<FlagIcon countryCode={option.countryCode} />}
              key={`${option.countryCode}-${option.topCurrencyGroup}`}
              value={option.currencyCode}
              data-testid={`currency-code-${option.currencyCode}`}
              disabled={disabledOption === option.currencyCode}
            >
              {option.currencyCode} {option.name}
            </Select.Option>
          )
        })}
      </Select>
    </div>
  )
}
